import {
	DELIVERY_METHOD_KIND,
	type DeliveryMethodKind,
} from "@dransay/api/models";
import { useCallback, useEffect, useState } from "react";
import {
	isHeaderCardDismissed,
	markHeaderCardDismissed,
} from "./express-courier-availability-cache";
import { useExpressCourierPharmacies } from "./use-express-courier-pharmacies";

interface UseHeaderExpressAvailabilityCardParams {
	/** Rollout flag AND an authenticated customer. */
	enabled: boolean;
	postalCode: string | undefined;
	expressVendorIds: readonly string[];
	selectedDeliveryMethodKind: DeliveryMethodKind;
}

/**
 * Owns whether the header's available-now promo card (`isVisible`) or the
 * outside-hours informational card (`isOutsideHours`) is shown for the
 * customer's current postal code. Both share the same gating matrix
 * (FR-045/FR-055); only the status branch differs.
 *
 * Gated on the Courier selection so the header never issues an express
 * `/check` while Shipping or Pickup is selected (FR-046). Dismissal is
 * re-derived from the session cache whenever the postal code changes,
 * mirroring the PLP modal's seen-marker re-arm pattern, but under its own
 * key so the two surfaces never suppress one another. `isOutsideHours`
 * never reads or writes that marker (FR-056) — it is not dismissible.
 */
export function useHeaderExpressAvailabilityCard({
	enabled,
	postalCode,
	expressVendorIds,
	selectedDeliveryMethodKind,
}: UseHeaderExpressAvailabilityCardParams): {
	isVisible: boolean;
	isOutsideHours: boolean;
	isLoading: boolean;
	dismiss: () => void;
} {
	const isCourierSelected =
		selectedDeliveryMethodKind === DELIVERY_METHOD_KIND.COURIER;
	const canCheck =
		enabled && isCourierSelected && !!postalCode && expressVendorIds.length > 0;

	const [dismissed, setDismissed] = useState(
		() => !!postalCode && isHeaderCardDismissed(postalCode),
	);

	useEffect(() => {
		setDismissed(!!postalCode && isHeaderCardDismissed(postalCode));
	}, [postalCode]);

	const { status, isLoading } = useExpressCourierPharmacies({
		postalCode,
		vendorIds: expressVendorIds,
		enabled: canCheck,
	});

	const dismiss = useCallback(() => {
		if (!postalCode) {
			return;
		}
		markHeaderCardDismissed(postalCode);
		setDismissed(true);
	}, [postalCode]);

	return {
		isVisible: canCheck && status === "available" && !dismissed,
		// Not dismissible (FR-056): the outside-hours card states a currently
		// true fact rather than a promotion, so the dismissal marker never
		// applies to it.
		isOutsideHours: canCheck && status === "unavailable_time",
		isLoading: canCheck && isLoading,
		dismiss,
	};
}
